import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/organization';
import { Dialog } from '@headlessui/react';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';
import {
  trackOrganizationCreateConfirmOpen,
  trackOrganizationCreated,
  trackOrganizationJoinCodeSubmit,
} from '../../analytics';

type CreateState = 'idle' | 'submitting';

const MAX_LEN = 80;

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')                 // drop apostrophes
    .replace(/[^a-z0-9]+/g, '-')          // spaces & symbols → dash
    .replace(/^-+|-+$/g, '')              // trim dashes
    .slice(0, 80);

const sanitize = (s: string) => s.replace(/\s+/g, ' ').trim();

const Organization: React.FC = () => {
  const [orgName, setOrgName] = useState('');
  const [state, setState] = useState<CreateState>('idle');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { setOrganization } = useOrganization();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOrgName('');
        setInviteCode('');
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  const emailLocal = useMemo(() => (user?.email || '').split('@')[0], [user?.email]);

  const suggestions = useMemo(() => {
    const base = emailLocal ? `${emailLocal}` : 'team';
    return [
      `${base.charAt(0).toUpperCase() + base.slice(1)} Labs`,
      'CivAPI Demo Org',
      'Data & Insights',
      'Operations',
    ];
  }, [emailLocal]);

  const slug = useMemo(() => toSlug(orgName || 'your-org'), [orgName]);

  const canSubmit = useMemo(() => {
    const s = sanitize(orgName);
    return s.length >= 2 && s.length <= MAX_LEN && state !== 'submitting';
  }, [orgName, state]);

  const handlePickSuggestion = (name: string) => setOrgName(name);

  const confirmCreate = () => {
    if (!canSubmit) {
      toast.error('Enter a valid organization name');
      return;
    }
    trackOrganizationCreateConfirmOpen({ name_length: sanitize(orgName).length });
    setConfirmOpen(true);
  };

  const createOrg = async () => {
    if (state === 'submitting') return;
    const clean = sanitize(orgName);
    if (!clean) {
      toast.error('Please enter an organization name');
      return;
    }
    if (clean.length > MAX_LEN) {
      toast.error(`Name must be ≤ ${MAX_LEN} characters`);
      return;
    }

    setState('submitting');
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/organization`,
        { name: clean, user_info: user },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setOrganization(data.organization);
      toast.success('Organization created 🎉');
      trackOrganizationCreated();
      navigate('/');
    } catch (error: any) {
      toast.error(handleApiError(error));
    } finally {
      setState('idle');
      setConfirmOpen(false);
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') confirmCreate();
  };

  const handleJoinByCode = async () => {
    const code = inviteCode.trim();
    if (!code) {
      toast.error('Paste your invite code first');
      return;
    }
    trackOrganizationJoinCodeSubmit({ code_length: code.length });
    setJoining(true);
    // just navigate; your AcceptInvitePage handles fetching/validation
    navigate(`/join/${encodeURIComponent(code)}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 px-4 py-12 md:grid-cols-2 md:gap-10">
        {/* Create card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-slate-100">
          <h1 className="mb-2 text-2xl font-bold text-slate-900">Create a New Organization</h1>
          <p className="mb-6 text-sm text-slate-600">
            Spin up a workspace for your team. You can invite members later from the Members tab.
          </p>

          <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="org-name">
            Organization name
          </label>
          <input
            id="org-name"
            ref={inputRef}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-blue-600"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            onKeyDown={handleEnter}
            placeholder="e.g. Apollo Analytics"
            maxLength={MAX_LEN}
            autoComplete="organization"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>Slug preview: <span className="font-mono text-slate-700">{slug}</span></span>
            <span>{orgName.length}/{MAX_LEN}</span>
          </div>

          {/* Suggestions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handlePickSuggestion(s)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
              >
                {s}
              </button>
            ))}
          </div>

          <button
            onClick={confirmCreate}
            disabled={!canSubmit}
            className={[
              'mt-6 w-full rounded-xl px-4 py-2.5 font-semibold text-white shadow-md transition',
              canSubmit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'
            ].join(' ')}
          >
            {state === 'submitting' ? (
              <span className="inline-flex items-center">
                <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                </svg>
                Creating…
              </span>
            ) : (
              'Create'
            )}
          </button>

          <p className="mt-3 text-xs text-slate-500">
            By creating, you agree to our&nbsp;
            <a href="https://static.civapi.com/terms-of-use" target="_blank" rel="noreferrer" className="text-blue-600 underline">
              Terms
            </a>
            &nbsp;and&nbsp;
            <a href="https://static.civapi.com/privacy-policy" target="_blank" rel="noreferrer" className="text-blue-600 underline">
              Privacy Policy
            </a>.
          </p>
        </div>

        {/* Join card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-slate-100">
          <h2 className="mb-2 text-2xl font-bold text-slate-900">Join an Existing Organization</h2>
          <p className="mb-4 text-sm text-slate-600">
            Ask an admin to send you an invite. If you already have a code, paste it here.
          </p>

          <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="invite-code">
            Invite code
          </label>
          <input
            id="invite-code"
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-blue-600"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="e.g. 8b2c7f-..."
          />

          <div className="mt-4 flex gap-3">
            <button
              onClick={handleJoinByCode}
              disabled={!inviteCode.trim() || joining}
              className={[
                'rounded-xl px-4 py-2.5 font-semibold text-white shadow-md transition',
                inviteCode.trim() && !joining ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-300 cursor-not-allowed'
              ].join(' ')}
            >
              {joining ? 'Opening…' : 'Join with Code'}
            </button>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            You can also accept via a link: <span className="font-mono text-slate-700">/join/&lt;code&gt;</span>
          </p>
        </div>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={setConfirmOpen} className="relative z-[9999]">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 grid place-items-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-100">
            <Dialog.Title className="text-base font-semibold text-slate-900">
              Create “{sanitize(orgName)}”?
            </Dialog.Title>
            <p className="mt-2 text-sm text-slate-600">
              We’ll create a new CivAPI organization for your account.
              <br />
              <span className="text-slate-500">Slug:</span>{' '}
              <span className="font-mono text-slate-800">{slug}</span>
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={createOrg}
                disabled={state === 'submitting'}
                className={[
                  'rounded-full px-5 py-2 text-sm font-semibold text-white',
                  state === 'submitting' ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                ].join(' ')}
              >
                {state === 'submitting' ? 'Creating…' : 'Create'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default Organization;
